'use client'

import { memo, useCallback } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { NinxlMagneticCta } from '@/app/ninxl/_components/ninxl-hero-entrance'
import {
  NINK_HERO_DEMO_SRC,
  NinxlHeroDemoPlayer,
} from '@/app/ninxl/_components/ninxl-hero-demo-player'
import { NinkProductivityScene } from './nink-productivity-scene'

export interface NinkHeroVisualProps {
  onDownload?: () => void
}

const CONTEXT_CHIPS = ['Slack', 'Docs', 'Mail', 'Notes', 'IDE'] as const

/**
 * baby: Premium stage with the 8-bit desk scene inside: chrome stays soft.
 */
export const NinkHeroVisual = memo(function NinkHeroVisual({
  onDownload,
}: NinkHeroVisualProps) {
  const shouldReduce = useReducedMotion()

  const handleDownload = useCallback(() => {
    onDownload?.()
  }, [onDownload])

  return (
    <div className="relative mx-auto w-full max-w-[1040px] pt-2">
      <div className="nink-hybrid-stage relative">
        {/* Soft status pill: 8-bit seasoning, not a HUD stack */}
        <div className="absolute left-4 top-4 z-20 md:left-6 md:top-6">
          {shouldReduce ? (
            <div className="nink-hybrid-chip">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--ninxl-brand-teal)]" aria-hidden />
              Live · speak → type
            </div>
          ) : (
            <motion.div
              className="nink-hybrid-chip"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <span
                className="ninxl-8bit-blink h-1.5 w-1.5 rounded-full bg-[color:var(--ninxl-brand-teal)]"
                aria-hidden
              />
              Live · speak → type
            </motion.div>
          )}
        </div>

        <div className="relative aspect-[16/9] w-full min-h-[280px] md:min-h-[420px]">
          <NinkProductivityScene />
          <div className="ninxl-crt-overlay pointer-events-none absolute inset-0" />
          <div className="ninxl-crt-scanline-live pointer-events-none absolute inset-0" />
          <NinxlHeroDemoPlayer
            variant="8bit"
            className="ninxl-hero-demo-on-monitor"
            demoSrc={NINK_HERO_DEMO_SRC}
            ariaLabel="Play NINK hero demo"
            dialogTitle="NINK hero demo"
          />
        </div>

        <div className="grid gap-0 border-t border-[color:var(--ninxl-theme-border-soft,var(--ninxl-8bit-dim))] md:grid-cols-[1.4fr_1fr]">
          <div className="p-5 md:border-r md:border-[color:var(--ninxl-theme-border-soft,var(--ninxl-8bit-dim))] md:p-6">
            <div className="nink-hybrid-eyebrow mb-2">The move</div>
            <p className="nink-hybrid-body ninxl-theme-text text-base leading-relaxed md:text-lg">
              Hold Shift, talk, release: text types into the frontmost app.
              Switch Slack, Docs, Mail, Notes; everything stays on device.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {CONTEXT_CHIPS.map((chip, index) =>
                shouldReduce ? (
                  <span key={chip} className="nink-hybrid-chip">
                    {chip}
                  </span>
                ) : (
                  <motion.span
                    key={chip}
                    className="nink-hybrid-chip"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.55 + index * 0.05 }}
                  >
                    {chip}
                  </motion.span>
                ),
              )}
            </div>
          </div>
          <div className="bg-[color:var(--ninxl-8bit-bg)]/50 p-5 md:p-6">
            <div className="nink-hybrid-eyebrow mb-3">On the desk</div>
            <ul className="nink-hybrid-body space-y-2 text-sm ninxl-theme-text">
              <li>Person speaking</li>
              <li>Apps switch on screen</li>
              <li>Keys stay idle</li>
              <li>Local · on device</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center justify-center gap-3">
        <NinxlMagneticCta>
          <button
            type="button"
            className="nink-hybrid-btn nink-hybrid-btn-primary"
            onClick={handleDownload}
          >
            Download NINK
          </button>
        </NinxlMagneticCta>
        <div className="nink-hybrid-status">Mac + Windows · speak once · write where you work</div>
      </div>
    </div>
  )
})
